import { useEffect, useMemo, useRef, useState } from 'react';
import { Marker, Polyline, Popup, useMap } from 'react-leaflet';
import L from 'leaflet';
import { format } from 'date-fns';
import { es } from 'date-fns/locale';
import type { TripRoute } from '../../types/trip';
import { formatRelative } from '../../utils/formatters';
import { routeStartIcon, routeEndIcon, createPlaybackVehicleIcon, routeStopIcon, ROUTE_LINE_COLOR } from './routeIcons';
import { buildTraceRuns, buildTraceTip, findLastIndexAtOrBefore, type InterpolatedPosition, type RouteStop, type TraceRun } from '../../utils/vehicleHistory';

// FE-26 — "ubicar vehículo". FE-27 (2026-09-20, pedido del dueño): ya no es un
// salto único que deja el mapa fijo mientras el vehículo se va, sino un modo de
// SEGUIMIENTO: el mapa se mueve a la par del vehículo.
//
// Decisiones (detalle laxo, anotadas):
//   · Un intervalo (`FOLLOW_INTERVAL_MS`), no un efecto por tick: la reproducción
//     actualiza la posición cada 50 ms, y recentrar el mapa (`moveend`) hace que
//     Leaflet recorte y re-proyecte los trazos; con hasta 20.000 vértices, a 20 Hz
//     sería trabajo de más. A 10 Hz el paso es de unos pocos px y no se nota.
//     Además el intervalo recoge sin más el salto de una búsqueda con la barra
//     de progreso, aunque la reproducción esté en pausa.
//   · Solo se mueve si el vehículo se apartó del centro (> 0,5 px): en pausa no
//     hace nada.
//   · Se salta mientras Leaflet anima un zoom (mismo problema que
//     `useFrozenWhileZooming`: mover el mapa a mitad de la animación); al terminar,
//     el siguiente intervalo recentra.
//   · Mientras se sigue, el zoom se hace alrededor del CENTRO (`'center'`):
//     con el zoom por defecto (alrededor del cursor) el vehículo se iría del
//     centro en cada rueda del ratón. Se restaura al dejar de seguir.
//   · Arrastrar el mapa (`dragstart`) apaga el seguimiento: si el usuario mueve el
//     mapa es que quiere mirar otra cosa, y recentrarlo a la fuerza pelearía con
//     su mano. `dragstart` lo disparan solo los gestos del usuario, no `setView`.
//
// 🚨 `setView` con `animate: false`, no `panTo`/`flyTo`: la animación usa
// `requestAnimationFrame`, que puede quedar congelado con la ventana ocluida
// (mismo hallazgo que llevó a `useRoutePlayback` a `setInterval`) — el
// seguimiento se vería roto sin ningún error.
const FOLLOW_INTERVAL_MS = 100;
const FOLLOW_MIN_ZOOM = 15;

function FollowPlayback({
  position, following, onStopFollowing,
}: { position: InterpolatedPosition | null; following: boolean; onStopFollowing: (() => void) | undefined }) {
  const map = useMap();
  // Refs: el efecto de abajo NO debe reiniciarse en cada tick de la reproducción.
  const positionRef = useRef(position);
  positionRef.current = position;
  const stopRef = useRef(onStopFollowing);
  stopRef.current = onStopFollowing;
  const zoomingRef = useRef(false);

  useEffect(() => {
    const onStart = () => { zoomingRef.current = true; };
    const onEnd = () => { zoomingRef.current = false; };
    map.on('zoomstart', onStart);
    map.on('zoomend', onEnd);
    return () => {
      map.off('zoomstart', onStart);
      map.off('zoomend', onEnd);
    };
  }, [map]);

  useEffect(() => {
    if (!following) return;

    // Al activar: centrar ya, y acercar si el mapa está lejos (mismo mínimo que el
    // "ubicar" de FE-26). Si el usuario ya tiene más zoom, se respeta.
    const first = positionRef.current;
    if (first) map.setView([first.lat, first.lng], Math.max(map.getZoom(), FOLLOW_MIN_ZOOM), { animate: false });

    const recenter = () => {
      const p = positionRef.current;
      if (!p || zoomingRef.current) return;
      const at = map.latLngToContainerPoint([p.lat, p.lng]);
      const mid = map.getSize().divideBy(2);
      if (Math.abs(at.x - mid.x) < 0.5 && Math.abs(at.y - mid.y) < 0.5) return;
      map.setView([p.lat, p.lng], map.getZoom(), { animate: false });
    };
    const id = setInterval(recenter, FOLLOW_INTERVAL_MS);
    const onDrag = () => stopRef.current?.();
    map.on('dragstart', onDrag);

    const { scrollWheelZoom, touchZoom, doubleClickZoom } = map.options;
    map.options.scrollWheelZoom = 'center';
    map.options.touchZoom = 'center';
    map.options.doubleClickZoom = 'center';

    return () => {
      clearInterval(id);
      map.off('dragstart', onDrag);
      map.options.scrollWheelZoom = scrollWheelZoom;
      map.options.touchZoom = touchZoom;
      map.options.doubleClickZoom = doubleClickZoom;
    };
  }, [following, map]);

  return null;
}

// FE-25 — mismo patrón que `TripDetailModal.tsx` (FE-11): al llegar un
// histórico nuevo, encuadra el mapa a su recorrido.
function HistoryRouteFitBounds({ points }: { points: [number, number][] }) {
  const map = useMap();
  useEffect(() => {
    if (points.length > 0) {
      map.fitBounds(L.latLngBounds(points), { padding: [40, 40] });
    }
  }, [map, points]);
  return null;
}

/**
 * FE-27 (2026-09-20) — una corrida del trazo: sólida si es MEDIDA, punteada si
 * es una recta ESTIMADA (el equipo no reportó por más de 2 min y el tramo cubre
 * más de 200 m; ver `isEstimatedHop`). Así lo que el mapa dibuja recto porque
 * faltan puntos no se confunde con una carretera recta.
 *
 * Halo y línea son componentes aparte porque se pintan en dos pasadas (todos
 * los halos, luego todas las líneas): si cada corrida pintara su halo junto a
 * su línea, el halo de una oscurecería la unión con la anterior.
 *
 * `lineCap="butt"` en el punteado: con la punta redonda por defecto los guiones
 * cortos se funden en puntos.
 */
const DASH = '8 10';

function TraceHalo({ run }: { run: TraceRun }) {
  return run.estimated
    ? <Polyline positions={run.positions} color="#000000" weight={7} opacity={0.3} dashArray={DASH} lineCap="butt" />
    : <Polyline positions={run.positions} color="#000000" weight={8} opacity={0.4} />;
}

function TraceLine({ run }: { run: TraceRun }) {
  return run.estimated
    ? <Polyline positions={run.positions} color={ROUTE_LINE_COLOR} weight={4} opacity={0.95} dashArray={DASH} lineCap="butt" />
    : <Polyline positions={run.positions} color={ROUTE_LINE_COLOR} weight={5} opacity={0.95} />;
}

/**
 * FE-27 (2026-09-20) — congela `value` mientras el mapa anima un zoom.
 *
 * Hallazgo del dueño: "al hacer zoom o alejar las líneas se desajustan de la
 * ruta". Reproducido montando el mapa real con la reproducción en marcha: la
 * punta de la línea se separaba del vehículo hasta 50 px al acercar y 20 px al
 * alejar durante los ~250 ms de la animación, y se corregía sola al terminar
 * (0 px). Con la reproducción en pausa no pasaba: solo con ticks activos.
 *
 * Causa: cada tick (50 ms) llama a `setLatLngs` de la punta y a `setLatLng` del
 * marcador en mitad de la animación de zoom de Leaflet. Los marcadores se
 * animan uno a uno hacia su posición nueva y el SVG de los trazos se escala
 * entero; reposicionar un marcador a mitad de camino lo saca de esa
 * coreografía. No es un problema del dato ni de la proyección.
 *
 * Arreglo: durante la animación se sigue mostrando la última posición previa al
 * zoom, y al terminar (`zoomend`) se vuelve a la actual. La reproducción NO se
 * detiene —solo lo que se pinta espera ~250 ms—. El arranque se marca en un
 * `ref` (síncrono en el evento) y no en estado: con estado, el tick que llega
 * entre `zoomstart` y el re-render aún movería el marcador.
 */
function useFrozenWhileZooming<T>(value: T): T {
  const map = useMap();
  const zoomingRef = useRef(false);
  const frozenRef = useRef(value);
  const [, rerender] = useState(0);

  useEffect(() => {
    const onStart = () => { zoomingRef.current = true; };
    const onEnd = () => {
      zoomingRef.current = false;
      rerender(n => n + 1); // con la reproducción en pausa no hay ticks: sin esto no se sincronizaría
    };
    map.on('zoomstart', onStart);
    map.on('zoomend', onEnd);
    return () => {
      map.off('zoomstart', onStart);
      map.off('zoomend', onEnd);
    };
  }, [map]);

  if (!zoomingRef.current) frozenRef.current = value;
  return frozenRef.current;
}

interface RouteHistoryLayersProps {
  /** FE-25 — recorrido histórico a dibujar. */
  route: TripRoute | null | undefined;
  /** FE-26 — posición interpolada de la reproducción animada. */
  playbackPosition: InterpolatedPosition | null | undefined;
  /** FE-26 — paradas detectadas del recorrido actual. */
  stops: RouteStop[] | undefined;
  /** FE-27 — el mapa acompaña al vehículo (modo "Ubicar"). */
  following: boolean | undefined;
  /** FE-27 — el usuario arrastró el mapa: quiere mirar otra cosa, se apaga el seguimiento. */
  onStopFollowing: (() => void) | undefined;
}

/**
 * FE-27 — capas del histórico de recorrido, extraídas de `MapView` (FE-25/26)
 * para que las compartan las dos carcasas: el mapa de Monitoreo en desktop y el
 * mapa de la página propia de mobile. Va **dentro** de un `MapContainer`
 * (usa `useMap`). No sabe nada de vehículos en vivo, cluster ni leyenda.
 */
export function RouteHistoryLayers({ route, playbackPosition: livePosition, stops, following, onStopFollowing }: RouteHistoryLayersProps) {
  // Lo que se PINTA (trazo, punta, marcador) usa la posición congelada durante el
  // zoom; el seguimiento ("Ubicar") usa la viva (`livePosition`).
  const playbackPosition = useFrozenWhileZooming(livePosition);
  const historyPoints = useMemo<[number, number][]>(
    () => (route?.points ?? []).map(p => [p.lat, p.lng] as [number, number]),
    [route],
  );

  // FE-26 — hallazgo real: entre dos puntos puede haber horas o días sin
  // reportar (equipo apagado, fuera del rango). Unirlos con una sola
  // polyline dibuja una línea recta que cruza el mapa por donde el vehículo
  // nunca estuvo (medido: 24 km en línea recta tras 87h sin datos). Se corta
  // en tramos y cada uno se dibuja por separado, sin conectarlos
  // (`buildTraceRuns`, `utils/vehicleHistory.ts`).
  //
  // 🚨 Una corrida siempre trae >= 2 vértices: un tramo de un solo punto (una
  // lectura aislada entre dos huecos) no es una línea — `L.Polyline` con un
  // solo vértice rompe el redibujado del *renderer* SVG compartido y deja
  // TODOS los tramos en blanco (`d="M0 0"`), no solo el suyo.
  //
  // 🚨 Traza progresiva (pedido del dueño 2026-09-19, ratificado en FE-27): el
  // recorrido NO se dibuja completo desde que se consulta — solo hasta
  // `playbackPosition`.
  //
  // FE-27 (seguimiento 2026-09-20) — la traza se parte en DOS para que la
  // reproducción no re-proyecte miles de vértices cada 50 ms (con
  // `maxPoints=20000` son hasta 20k):
  //   · `runs`: los tramos ya PASADOS. Depende de `lastIdx` (el último punto
  //     real ya recorrido), no del instante exacto → conserva la identidad entre
  //     ticks y react-leaflet no llama `setLatLngs` hasta que la reproducción
  //     cruza un punto real.
  //   · `tip`: del último punto real a la posición interpolada. Son 2 vértices
  //     y sí cambia en cada tick.
  // Junto con estilo: sólido = medido, punteado = estimado (`isEstimatedHop`).
  const times = useMemo(() => (route?.points ?? []).map(p => Date.parse(p.occurredAt)), [route]);
  const lastIdx = playbackPosition ? findLastIndexAtOrBefore(times, Date.parse(playbackPosition.occurredAt)) : -1;
  const runs = useMemo<TraceRun[]>(() => (route ? buildTraceRuns(route.points, lastIdx) : []), [route, lastIdx]);
  // Identidades estables (`useMemo`): react-leaflet llama `setLatLngs`/`setLatLng`/`setIcon`
  // cuando la referencia cambia, no cuando el valor cambia. Con un objeto nuevo en
  // cada render, esos setters corrían cada tick aunque la posición estuviera
  // congelada, y `setIcon` recreaba el marcador 20 veces por segundo.
  const lat = playbackPosition?.lat;
  const lng = playbackPosition?.lng;
  const bearing = playbackPosition ? Math.round(playbackPosition.bearing) : null;
  const tip = useMemo(
    () => (route && lat !== undefined && lng !== undefined ? buildTraceTip(route.points, lastIdx, { lat, lng }) : null),
    [route, lastIdx, lat, lng],
  );
  const traces = useMemo(() => (tip ? [...runs, tip] : runs), [runs, tip]);
  const vehiclePosition = useMemo<[number, number] | null>(() => (lat !== undefined && lng !== undefined ? [lat, lng] : null), [lat, lng]);
  const vehicleIcon = useMemo(() => (bearing !== null ? createPlaybackVehicleIcon(bearing) : null), [bearing]);

  return (
    <>
      {route && historyPoints.length > 0 && (
        <>
          <HistoryRouteFitBounds points={historyPoints} />
          {traces.map((run, i) => <TraceHalo key={`halo-${i}`} run={run} />)}
          {traces.map((run, i) => <TraceLine key={`line-${i}`} run={run} />)}
          <Marker position={historyPoints[0]} icon={routeStartIcon}>
            <Popup>
              <div style={{ padding: '4px' }}>
                <p style={{ margin: 0, fontWeight: 800, color: '#10B981', fontSize: '12px' }}>🏁 PRIMER PUNTO MEDIDO</p>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>{formatRelative(route.points[0].occurredAt)}</p>
              </div>
            </Popup>
          </Marker>
          <Marker position={historyPoints[historyPoints.length - 1]} icon={routeEndIcon}>
            <Popup>
              <div style={{ padding: '4px' }}>
                <p style={{ margin: 0, fontWeight: 800, color: '#EF4444', fontSize: '12px' }}>📍 ÚLTIMO PUNTO MEDIDO</p>
                <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>{formatRelative(route.points[route.points.length - 1].occurredAt)}</p>
              </div>
            </Popup>
          </Marker>
        </>
      )}

      {vehiclePosition && vehicleIcon && (
        <Marker
          position={vehiclePosition}
          icon={vehicleIcon}
          zIndexOffset={1000}
        />
      )}

      <FollowPlayback position={livePosition ?? null} following={!!following} onStopFollowing={onStopFollowing} />

      {stops?.map((stop, i) => (
        <Marker key={`${stop.arrivedAt}-${i}`} position={[stop.lat, stop.lng]} icon={routeStopIcon}>
          <Popup>
            <div style={{ padding: '4px' }}>
              <p style={{ margin: 0, fontWeight: 800, color: '#F59E0B', fontSize: '12px' }}>🅿️ PARADA</p>
              <p style={{ margin: '4px 0 0', fontSize: '11px' }}>
                Llegó: {format(new Date(stop.arrivedAt), 'dd/MM HH:mm', { locale: es })}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '11px' }}>
                Salió: {format(new Date(stop.leftAt), 'dd/MM HH:mm', { locale: es })}
              </p>
              <p style={{ margin: '2px 0 0', fontSize: '11px', color: '#6B7280' }}>
                {stop.durationMin >= 60
                  ? `${Math.floor(stop.durationMin / 60)} h ${Math.round(stop.durationMin % 60)} min detenido`
                  : `${Math.round(stop.durationMin)} min detenido`}
              </p>
            </div>
          </Popup>
        </Marker>
      ))}
    </>
  );
}
